"use client";

import { useEffect, useRef, useState } from "react";

interface UseIntersectionObserverOptions {
  threshold?: number;
  rootMargin?: string;
  enabled?: boolean;
}

/**
 * Hook для відстеження видимості елемента через Intersection Observer API
 * Використовується для lazy loading компонентів
 */
export function useIntersectionObserver({
  threshold = 0.1,
  rootMargin = "100px", // Починає завантажувати за 100px до появи в viewport
  enabled = true,
}: UseIntersectionObserverOptions = {}) {
  const [hasIntersected, setHasIntersected] = useState(false);
  const elementRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!enabled) return;

    const element = elementRef.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setHasIntersected(true);
            // Відключаємо observer після першого появи
            observer.unobserve(entry.target);
          }
        });
      },
      {
        threshold,
        rootMargin,
      }
    );

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [threshold, rootMargin, enabled]);

  return { elementRef, isVisible: !enabled || hasIntersected };
}
